import { IconButton, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { groupBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { resolveQuantity } from '../../../domain/property-operations';
import { ElementIdentifierDbidType } from '../../../gql/graphql';
import { usePropertyContext } from '../../../hooks/property-resolving';
import { difference } from '../../../utils/dbid-utils';
import { useViewer } from '../../common/ForgeViewer';
import SparkelIcon from '../../common/icon/SparkelIcon';
import {
  SPARKEL_GEOMETRIC_PROPERTY_SET,
  SPARKEL_LOCATION_PROPERTY_SET,
} from '../../../domain/geometry/geometric-types';
import { displayNameFromProperty } from '../../../domain/friendly-properties';
import { TableEntry } from './OrderEntriesPanel';

const MissingPropertyButton = ({
  projectId,
  resolvedForDbIds,
  resolvedForQuantity,
  isDisabled,
}: {
  projectId: string;
  resolvedForDbIds: NonNullable<TableEntry['resolvedElementIdentifiersDbid']>;
  resolvedForQuantity: NonNullable<TableEntry['quantity']>;
  isDisabled?: boolean;
}) => {
  const { propertyContext, error, isAllPropertiesLoaded } =
    usePropertyContext(projectId);

  const { selectDbIds } = useViewer();

  const { t } = useTranslation(['common', 'table']);

  const disabledIconColor = useColorModeValue('gray.50', 'gray.600');

  const isolateMissingElements = async () => {
    const resolvedQuantity = await resolveQuantity(
      propertyContext,
      resolvedForDbIds,
      resolvedForQuantity,
      {
        maxTrianglesForComplexAlgorithm: 5000,
        globalOffset: [0, 0, 0],
        unitSettings: undefined,
      }
    );
    if (!resolvedQuantity.dataPoints) {
      throw 'Missing data points for resolved quantity';
    }
    const resultDbIds: ElementIdentifierDbidType[] = Object.entries(
      groupBy(resolvedQuantity.dataPoints, (dataPoint) => dataPoint.modelUrn)
    ).map(([modelUrn, dataPoints]) => ({
      modelUrn,
      dbIds: dataPoints.map((dataPoint) => dataPoint.dbId),
    }));
    const missingDbIds = difference(resolvedForDbIds, resultDbIds);
    selectDbIds(
      Object.fromEntries(
        missingDbIds.map((dbId) => [dbId.modelUrn, dbId.dbIds])
      )
    );
  };

  const selectedAttribute = resolvedForQuantity.calculation?.attribute;
  const propertyName =
    selectedAttribute?.category === SPARKEL_GEOMETRIC_PROPERTY_SET ||
    selectedAttribute?.category === SPARKEL_LOCATION_PROPERTY_SET
      ? displayNameFromProperty(selectedAttribute.name, t)
      : selectedAttribute?.name;

  return (
    <Tooltip
      label={t('table:select-elements-missing-property', {
        propertyName,
      })}
      isDisabled={isDisabled}
    >
      <IconButton
        aria-label={
          'Select elements missing property ' + selectedAttribute?.name
        }
        variant="ghost"
        isDisabled={isDisabled || !isAllPropertiesLoaded || !!error}
        icon={
          <SparkelIcon
            icon={faExclamationCircle}
            color={isDisabled ? disabledIconColor : 'orange.300'}
          />
        }
        isRound
        height="100%"
        size="sm"
        noOfLines={1}
        wordBreak="break-all"
        onClick={(e) => {
          isolateMissingElements();
          // Prevent row selection
          e.stopPropagation();
        }}
      />
    </Tooltip>
  );
};

export default MissingPropertyButton;
